import React, { Suspense, useCallback } from "react";
import { MicrositeHeading } from "pages/LayoutComponents/MicrositeHeading";
import { UniFiGPT } from "pages/LayoutComponents/UniFiGpt";
import { StyledContainer } from "../../../components/StyledContainer";
import { MainSection } from "./components/MainSection/MainSection";
import { Resources } from "./components/Resources/Resources";
import { useTranslation } from "hooks/useTranslation";
import {
  MainSection as MainSectionType,
  ResourceCard,
} from "./UniFiPartnerHub.types";
import Head from "layout/Head";
import * as S from "./UniFiPartnerHub.styles";
import { getPageBasePaths } from "context/SiteSettingsContext/utils";
import { useSiteSettings } from "context/SiteSettingsContext";
import { Route, Switch, useHistory } from "react-router-dom";

import dedicatedSupportPng from "./assets/images/dedicated-support.png";
import phoneSupportSvg from "./assets/icons/phone-support.svg";
import partnerDrivenTrainingSvg from "./assets/icons/partner-driven-training.svg";
import ambassadorAccessSvg from "./assets/icons/ambassador-access.svg";
import unifiChannelPartnerProgramPng from "./assets/images/unifi-channel-partner-program.png";
import uiCarePng from "./assets/images/ui-care.png";
import extendedCoverageSvg from "./assets/icons/extended-coverage.svg";
import freeReturnShippingSvg from "./assets/icons/free-return-shipping.svg";
import partnerTrainingResourcesPng from "./assets/images/partner-training-resources.png";
import certificationProgramSvg from "./assets/icons/certification-program.svg";
import onDemandLearningSvg from "./assets/icons/on-demand-learning.svg";
import caseStudiesSvg from "./assets/icons/case-studies.svg";
import eventAndProgramsSvg from "./assets/icons/event-and-programs.svg";
import technicalSpecificationSvg from "./assets/icons/technical-specification.svg";
import partnerNewsletterSvg from "./assets/icons/partner-newsletter.svg";
import { useLoadRecaptcha } from "hooks/useLoadRecaptcha";

const PartnersModalContent = React.lazy(() =>
  import("./components/PartnersModalContent").then(
    ({ PartnersModalContent }) => ({
      default: PartnersModalContent,
    }),
  ),
);

export const UniFiPartnerHub = () => {
  const { t } = useTranslation();

  const { routes } = useSiteSettings();
  const history = useHistory();

  useLoadRecaptcha();

  const getPath = useCallback(
    (path: string) => {
      try {
        const { pathname } = new URL(routes.uiComPage({ path })) ?? {};

        return pathname ?? "";
      } catch (e) {
        console.log(e);
      }

      return path;
    },
    [routes],
  );

  const onModalClose = () => {
    history.push({
      pathname: getPath("/partner-hub"),
      // @ts-expect-error this is custom property so error is expected
      skipScrollToTop: true,
    });
  };

  const MAIN_SECTIONS: MainSectionType[] = [
    {
      imgFirst: true,
      customWhiteSpaceTop: 84,
      header: {
        title: t("unifiPartnerHub.mainSection.1.title"),
        description: t("unifiPartnerHub.mainSection.1.description"),
      },
      img: {
        src: dedicatedSupportPng,
        alt: t("unifiPartnerHub.mainSection.1.title"),
      },
      points: [
        {
          icon: phoneSupportSvg,
          copy: {
            title: t("unifiPartnerHub.mainSection.1.point.1.title"),
            description: t("unifiPartnerHub.mainSection.1.point.1.description"),
          },
        },

        {
          icon: partnerDrivenTrainingSvg,
          copy: {
            title: t("unifiPartnerHub.mainSection.1.point.2.title"),
            description: t("unifiPartnerHub.mainSection.1.point.2.description"),
          },
        },

        {
          icon: ambassadorAccessSvg,
          copy: {
            title: t("unifiPartnerHub.mainSection.1.point.3.title"),

            description: t("unifiPartnerHub.mainSection.1.point.3.description"),
          },
        },
      ],
      buttonLink: "https://ui.com/professional-integrators",
    },

    {
      registerNowButton: true,
      header: {
        title: t("unifiPartnerHub.mainSection.2.title"),
        description: t("unifiPartnerHub.mainSection.2.description"),
      },
      img: {
        src: unifiChannelPartnerProgramPng,
        alt: t("unifiPartnerHub.mainSection.2.title"),
      },
      customPoints: {
        description: t("unifiPartnerHub.mainSection.2.point.description"),
        header: t("unifiPartnerHub.mainSection.2.point.header"),
        points: [
          {
            category: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.1.category",
            ),
            qualifiedProducts: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.1.products",
            ),
          },

          {
            category: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.2.category",
            ),
            qualifiedProducts: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.2.products",
            ),
          },

          {
            category: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.3.category",
            ),
            qualifiedProducts: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.3.products",
            ),
          },

          {
            category: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.4.category",
            ),
            qualifiedProducts: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.4.products",
            ),
          },

          {
            category: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.5.category",
            ),
            qualifiedProducts: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.5.products",
            ),
          },

          {
            category: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.6.category",
            ),
            qualifiedProducts: t(
              "unifiPartnerHub.mainSection.2.point.qualifiedProducts.6.products",
            ),
          },
        ],
      },
      buttonLink: getPath("/partner-hub/partner-program"),
    },

    {
      imgFirst: true,
      header: {
        title: t("unifiPartnerHub.mainSection.3.title"),
        description: t("unifiPartnerHub.mainSection.3.description"),
      },
      disclaimer: "Available Through Your Authorized Distributor",
      img: {
        src: uiCarePng,
        alt: t("unifiPartnerHub.mainSection.3.title"),
      },
      points: [
        {
          icon: phoneSupportSvg,
          copy: {
            title: t("unifiPartnerHub.mainSection.3.point.1.title"),
            description: t("unifiPartnerHub.mainSection.3.point.1.description"),
          },
        },

        {
          icon: extendedCoverageSvg,
          copy: {
            title: t("unifiPartnerHub.mainSection.3.point.2.title"),
            description: t("unifiPartnerHub.mainSection.3.point.2.description"),
          },
        },

        {
          icon: freeReturnShippingSvg,
          copy: {
            title: t("unifiPartnerHub.mainSection.3.point.3.title"),
            description: t("unifiPartnerHub.mainSection.3.point.3.description"),
          },
        },
      ],
      buttonLink: "https://ui.com/ui-care",
    },
    {
      header: {
        title: t("unifiPartnerHub.mainSection.4.title"),
        description: t("unifiPartnerHub.mainSection.4.description"),
      },

      img: {
        src: partnerTrainingResourcesPng,
        alt: t("unifiPartnerHub.mainSection.4.title"),
      },
      points: [
        {
          icon: certificationProgramSvg,
          copy: {
            title: t("unifiPartnerHub.mainSection.4.point.1.title"),
            description: t("unifiPartnerHub.mainSection.4.point.1.description"),
          },
        },

        {
          icon: onDemandLearningSvg,
          copy: {
            title: t("unifiPartnerHub.mainSection.4.point.2.title"),
            description: t("unifiPartnerHub.mainSection.4.point.2.description"),
          },
        },
      ],

      buttonLink: "https://www.ui.com/training/",
    },
  ];

  const RESOURCES: ResourceCard[] = [
    {
      icon: caseStudiesSvg,
      title: t("unifiPartnerHub.resources.1.title"),
      link: {
        text: t("unifiPartnerHub.resources.1.linkText"),
        href: "https://casestudies.ui.com/",
      },
    },

    {
      icon: eventAndProgramsSvg,
      title: t("unifiPartnerHub.resources.2.title"),
      link: {
        text: t("unifiPartnerHub.resources.2.linkText"),
        // TODO: is this correct?
        href: "https://uwc.ui.com/",
      },
    },

    {
      icon: technicalSpecificationSvg,
      title: t("unifiPartnerHub.resources.3.title"),
      link: {
        text: t("unifiPartnerHub.resources.3.linkText"),
        href: "https://techspecs.ui.com/",
      },
    },

    {
      icon: partnerNewsletterSvg,
      title: t("unifiPartnerHub.resources.4.title"),
      link: {
        text: t("unifiPartnerHub.resources.4.linkText"),
        // TODO: is this correct?
        href: "https://www.ui.com/newsletters/",
      },
    },
  ];

  return (
    <>
      <Head
        title={t("unifiPartnerHub.seo.title")}
        description={t("unifiPartnerHub.seo.description")}
        url="https://ui.com/partner-hub"
        image="https://www.ui.com/microsite/og-image.png"
      />
      <StyledContainer size="desktop" $spacing={{ top: 48, bottom: 100 }}>
        <MicrositeHeading
          isMainTitle
          title={t("unifiPartnerHub.title")}
          isCentered
          spacing={{ bottom: 32 }}
        />
        <UniFiGPT />
        {MAIN_SECTIONS.map((section, index) => (
          <MainSection
            section={section}
            key={`${section.header.title}-${index}`}
          />
        ))}
        <Resources resources={RESOURCES} />
      </StyledContainer>

      <Switch>
        <Route path={getPageBasePaths("/partner-hub/partner-program")}>
          <S.Modal
            isOpen
            onRequestClose={onModalClose}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            lockScroll
          >
            <Suspense fallback={null}>
              <PartnersModalContent />
            </Suspense>
          </S.Modal>
        </Route>
      </Switch>
      <S.GlobalModalStyle />
    </>
  );
};
